import React from "react";
import "../CustomTheme/customTheme.css";
import "./CustomSummaryCard.css";

const TONES = ["primary", "pink", "warning", "success", "info", "purple", "danger", "neutral"];

/**
 * CustomSummaryCard — one titled group of numbers, side by side with dividers.
 *
 *   ┌──────────── Total ────────────┐   (coloured accent on top)
 *   │   21    │    1     │  4.76%   │
 *   │ Registr │ Paid Use │ Day Conv │
 *   └───────────────────────────────┘
 *
 * Props:
 *  - title     group name, e.g. "Default"
 *  - metrics   [{ label, value, tooltip? }] — value already formatted ("1,204", "4.76%")
 *  - tone      "primary" (default) | "pink" | "warning" | "success" | "info" | "purple" | "danger" | "neutral"
 *  - loading   skeletons instead of the values
 *  - className, style
 */
const CustomSummaryCard = ({ title, metrics = [], tone = "primary", loading = false, className, style }) => {
  const safeTone = TONES.includes(tone) ? tone : "primary";
  return (
    <section
      className={`csum-card csum-${safeTone}${className ? ` ${className}` : ""}`}
      style={{ "--csum-cols": Math.max(1, metrics.length), ...style }}
      aria-label={title}
    >
      {title && <h3 className="csum-title">{title}</h3>}
      <dl className="csum-metrics">
        {metrics.map((m, i) => {
          // 0 is a real value; only a missing one shows "—" (cell and hover title agree)
          const shown = m.value === null || m.value === undefined || m.value === "" ? "—" : m.value;
          return (
            <div className="csum-metric" key={`${m.label}-${i}`} title={m.tooltip ?? (loading ? undefined : `${m.label}: ${shown}`)}>
              {/* label first for screen readers; CSS (column-reverse) shows the value on top */}
              <dt className="csum-label">{m.label}</dt>
              <dd className="csum-value">{loading ? <span className="csum-skeleton" aria-hidden="true" /> : shown}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
};

/**
 * CustomSummaryCards — a wrapping row of CustomSummaryCard; wider groups (more metrics) get more room.
 * A card left alone on a line is capped at half the row (like col-6); on phones every card is full width.
 *   <CustomSummaryCards loading={loading} groups={[{ title, tone, metrics }]} />
 */
export const CustomSummaryCards = ({ groups = [], loading = false, className }) => {
  const list = groups.filter(Boolean);
  if (!list.length) return null;
  return (
    <div className={`csum-row${className ? ` ${className}` : ""}`}>
      {list.map((g, i) => (
        <CustomSummaryCard
          key={`${g.title ?? ""}-${i}`} // titles can repeat (e.g. two blank source names)
          {...g}
          loading={loading}
          style={{ flex: `${g.metrics?.length || 1} 1 ${(g.metrics?.length || 1) * 110}px`, ...g.style }}
        />
      ))}
    </div>
  );
};

export default CustomSummaryCard;
